import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useContentWidth, WIDTH_CLASSES } from '../../hooks/useContentWidth';
import { supabase } from '../../lib/supabase';
import { embedOne } from '../../lib/utils';
import { AppSidebarShell } from './AppSidebarShell';
import { HostTabBar } from './HostTabBar';
import { UnsavedGuardProvider, useGuardedNavigation } from './unsavedGuard';
import type { UnsavedGuard } from './unsavedGuard';
import { Avatar, ConfirmDialog, NavItem, StatusBadge } from '../design-system';
import type { MatchStatus } from '../../types';

type BuildStatus = 'not_started' | 'draft' | 'submitted' | 'approved';

interface MatchMeta {
  travelerName: string | null;
  travelerAvatar: string | null;
  tripName: string | null;
  destination: string | null;
  status: MatchStatus;
  /** Date range label, e.g. "Jul 5–8". */
  dates: string | null;
  /** Handbook completion 0–100 (share of days with at least one place). */
  progress: number;
  /** Builder sub-state chip. */
  buildLabel: string;
}

interface TripOption {
  matchId: string;
  label: string;
  status: MatchStatus;
  /** Handbook build status — separate from `status` (the traveler<->host
   *  match state). The switcher's dot/checkmark track THIS, not the match
   *  status: coloring them by match status put green next to a trip whose
   *  handbook was still a plain Draft, which read as if it had been
   *  approved when it hadn't even been submitted. */
  buildStatus: BuildStatus;
}

/** Tone for a handbook's build status — the one color standard this card
 *  actually uses (gray = not started, violet = draft, gold = waiting on
 *  approval, green = approved). Never derive this from MatchStatus. */
function buildStatusToneClass(status: BuildStatus): string {
  if (status === 'approved') return 'text-success';
  if (status === 'submitted') return 'text-gold';
  if (status === 'draft') return 'text-violet';
  return 'text-app-muted';
}

function BuildStatusDot({ status }: { status: BuildStatus }) {
  const tone = status === 'approved' ? 'success' : status === 'submitted' ? 'gold' : status === 'draft' ? 'violet' : 'neutral';
  return <StatusBadge dotOnly tone={tone} className="mr-1.5" />;
}

interface MatchSwitcherCardProps {
  matchId: string;
  meta: MatchMeta;
  trips: TripOption[];
  onClose: () => void;
}

/** Traveler card + trip switcher rendered in the sidebar header slot. */
function MatchSwitcherCard({ matchId, meta, trips, onClose }: MatchSwitcherCardProps) {
  const navigate = useNavigate();
  const attempt = useGuardedNavigation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const hasMultiple = trips.length > 1;
  const travelerFirst = meta.travelerName?.split(' ')[0] ?? 'Traveler';

  return (
    <div className="mb-4">
      <button
        type="button"
        disabled={!hasMultiple}
        onClick={() => setSwitcherOpen((o) => !o)}
        className="w-full text-left"
        style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', cursor: hasMultiple ? 'pointer' : 'default' }}
      >
        {/* Traveler + trip + dates */}
        <div className="flex items-center gap-[9px] mb-2">
          <Avatar size={24} src={meta.travelerAvatar ?? undefined} name={travelerFirst} style={{ fontSize: 11 }} />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-medium text-app-fg whitespace-nowrap overflow-hidden text-ellipsis">
              {meta.tripName ?? meta.destination ?? `Trip with ${travelerFirst}`}
            </div>
            {meta.dates && <div className="text-[11px] text-app-muted mt-px">{meta.dates}</div>}
          </div>
          {hasMultiple && (
            <i className={`ti ti-chevron-down text-app-muted text-[13px] shrink-0 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} />
          )}
        </div>
        {/* Handbook progress — always violet, never success/green. This is
            draft completeness, not approval; the "Approved" chip below is
            the only place success green should appear on this card. */}
        <div className="h-1 rounded-sm overflow-hidden" style={{ background: 'var(--line)' }}>
          <div
            className="h-full rounded-sm transition-[width] duration-300"
            style={{ width: `${meta.progress}%`, background: 'rgb(var(--violet))' }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[11px] text-app-muted">{meta.progress}% built</span>
          <span
            className="text-[10px] font-medium rounded-full px-2 py-px"
            style={{
              background: meta.buildLabel === 'Approved' ? 'rgb(var(--success) / .11)' : 'var(--surface)',
              color: meta.buildLabel === 'Approved' ? 'var(--success-fg)' : 'var(--text-dim)',
              border: meta.buildLabel === 'Approved' ? '0.5px solid rgb(var(--success) / .28)' : '1px solid var(--line)',
            }}
          >
            {meta.buildLabel}
          </span>
        </div>
      </button>

      {hasMultiple && switcherOpen && (
        <div className="mt-1 bg-app-bg border border-app-border rounded-xl p-1 flex flex-col">
          <p className="text-[10px] uppercase tracking-widest text-app-muted font-medium px-2 py-1.5">Switch trip</p>
          {trips.map((t) => (
            <button
              key={t.matchId}
              type="button"
              onClick={() => {
                setSwitcherOpen(false);
                onClose();
                attempt(() => navigate(`/host/itinerary/${t.matchId}`));
              }}
              className={`flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-left transition-colors ${
                t.matchId === matchId ? 'bg-black/10 text-app-fg' : 'text-app-muted hover:text-app-fg hover:bg-black/5'
              }`}
            >
              <BuildStatusDot status={t.buildStatus} />
              <span className="truncate flex-1">{t.label}</span>
              {t.matchId === matchId && (
                // Same handbook-build-status tone as the dot on this row —
                // green only once that trip's handbook is actually approved.
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-3.5 h-3.5 shrink-0 ${buildStatusToneClass(t.buildStatus)}`}>
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Which part of a trip the host is in, from the URL. */
export function matchSectionFor(pathname: string): 'builder' | 'chat' | 'preview' | undefined {
  if (pathname.startsWith('/host/itinerary')) return 'builder';
  if (pathname.startsWith('/host/chat')) return 'chat';
  if (pathname.startsWith('/host/preview')) return 'preview';
  return undefined;
}

interface MatchNavContentProps {
  matchId: string;
  meta: MatchMeta | null;
  onClose: () => void;
}

function MatchNavContent({ matchId, meta, onClose }: MatchNavContentProps) {
  const navigate = useNavigate();
  const attempt = useGuardedNavigation();
  const { pathname } = useLocation();
  const travelerFirst = meta?.travelerName?.split(' ')[0] ?? 'Traveler';

  const items = [
    { key: 'dashboard', icon: 'layout-kanban', label: 'Dashboard', to: '/host' },
    { key: 'builder', icon: 'book', label: 'Handbook builder', to: `/host/itinerary/${matchId}` },
    { key: 'chat', icon: 'message-circle', label: `Chat with ${travelerFirst}`, to: `/host/chats/${matchId}` },
    { key: 'preview', icon: 'eye', label: 'Preview as traveler', to: `/host/preview/${matchId}` },
  ];

  const activeKey = matchSectionFor(pathname);

  return (
    <nav className="flex flex-col gap-[3px]">
      {items.map((it) => (
        <NavItem
          key={it.key}
          icon={it.icon}
          label={it.label}
          active={it.key === activeKey}
          onClick={() => { attempt(() => navigate(it.to)); onClose(); }}
        />
      ))}
    </nav>
  );
}

interface HostMatchLayoutProps {
  children: React.ReactNode;
  fullBleed?: boolean;
}

export function HostMatchLayout({ children, fullBleed }: HostMatchLayoutProps) {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { width } = useContentWidth();
  const [meta, setMeta] = useState<MatchMeta | null>(null);
  const [trips, setTrips] = useState<TripOption[]>([]);

  // Screens with editable state (the handbook builder) register here so any
  // way out — header, tabs, sidebar — can stop and ask first.
  const guardRef = useRef<UnsavedGuard | null>(null);
  const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);
  const [leaving, setLeaving] = useState(false);
  const setGuard = useCallback((g: UnsavedGuard | null) => { guardRef.current = g; }, []);
  const attempt = useCallback((go: () => void) => {
    if (guardRef.current?.dirty) setPendingExit(() => go);
    else go();
  }, []);
  const guardValue = { setGuard, attempt };

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data: matchRows } = await supabase
        .from('matches')
        .select('id, traveler_id, portrait_id, status')
        .eq('host_id', user.id)
        // Not 'proposed': a host only hears of a trip once the traveler chose and paid.
        .in('status', ['pending', 'accepted'])
        .order('created_at', { ascending: false });

      if (!matchRows || matchRows.length === 0) { setTrips([]); return; }

      const travelerIds = [...new Set(matchRows.map((m) => m.traveler_id as string))];
      const portraitIds = [...new Set(matchRows.map((m) => m.portrait_id as string).filter(Boolean))];
      const matchIds = matchRows.map((m) => m.id as string);

      const [{ data: names }, { data: itins }, { data: portraitRows }] = await Promise.all([
        supabase.from('profiles').select('id, full_name').in('id', travelerIds),
        supabase.from('itineraries').select('match_id, status, content').in('match_id', matchIds),
        portraitIds.length > 0
          ? supabase.from('taste_portraits').select('id, trip_name').in('id', portraitIds)
          : Promise.resolve({ data: [] }),
      ]);

      const nameMap = new Map((names ?? []).map((n) => [n.id as string, n.full_name as string | null]));
      const itinMap = new Map((itins ?? []).map((it) => [
        it.match_id as string,
        { status: it.status as string, content: it.content as Record<string, unknown> | null },
      ]));
      const portraitNameMap = new Map((portraitRows ?? []).map((p) => [p.id as string, p.trip_name as string | null]));

      setTrips(matchRows.map((m) => {
        const itin = itinMap.get(m.id as string) ?? null;
        const content = itin?.content ?? null;
        const tripName = (content ? String(content.trip_name ?? '') || null : null) ?? (m.portrait_id ? portraitNameMap.get(m.portrait_id as string) ?? null : null);
        const destination = content ? String(content.destination ?? '') || null : null;
        const travelerFirst = nameMap.get(m.traveler_id as string)?.split(' ')[0] ?? 'traveler';
        const buildStatus: BuildStatus =
          itin?.status === 'approved' ? 'approved' :
          itin?.status === 'submitted' ? 'submitted' :
          itin?.status === 'draft' ? 'draft' : 'not_started';
        return {
          matchId: m.id as string,
          status: m.status as MatchStatus,
          label: tripName ?? destination ?? `Trip with ${travelerFirst}`,
          buildStatus,
        };
      }));
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !matchId) return;

    // This layout renders once when a host navigates into a match and never
    // remounts while they build the handbook in the ItineraryEditor child
    // route — so without a way to hear about a save, "% built" here just
    // freezes at whatever it was on arrival (often 0%) no matter how much
    // the host actually drafts. ItineraryEditor dispatches this event after
    // every successful save so the sidebar card can catch up.
    function onItinerarySaved(e: Event) {
      const detail = (e as CustomEvent<{ matchId: string }>).detail;
      if (detail?.matchId === matchId) void loadMeta();
    }
    window.addEventListener('sk:itinerary-saved', onItinerarySaved);

    void loadMeta();

    async function loadMeta() {
      const { data: match } = await supabase
        .from('matches')
        .select(`
          status,
          traveler_profile:profiles!matches_traveler_id_fkey(full_name, avatar_url),
          itinerary:itineraries(status, content),
          portrait:taste_portraits(trip_name)
        `)
        .eq('id', matchId)
        .eq('host_id', user!.id)
        .maybeSingle();

      if (!match) return;
      const raw = match as Record<string, unknown>;
      const tp = raw.traveler_profile as { full_name: string | null; avatar_url: string | null } | null;
      const itin = embedOne(raw.itinerary as { status: string; content: Record<string, unknown> } | { status: string; content: Record<string, unknown> }[] | null);
      const content = (itin?.content ?? null) as Record<string, unknown> | null;
      // matches.portrait_id -> taste_portraits.id is a forward FK (many-to-one
      // from matches), so PostgREST embeds it as a single object — not an
      // array like the itinerary embed above (which is the reverse relation).
      const portrait = raw.portrait as { trip_name: string | null } | null;

      // Handbook completion: share of days with at least one place.
      const days = (content?.days as Array<{ places?: unknown[] }> | undefined) ?? [];
      const withPlaces = days.filter((d) => (d.places?.length ?? 0) > 0).length;
      const progress = days.length ? Math.round((withPlaces / days.length) * 100) : 0;

      const start = content?.start_date ? new Date(String(content.start_date)) : null;
      const end = content?.end_date ? new Date(String(content.end_date)) : null;
      const dates = start && end
        ? `${start.toLocaleDateString('en', { month: 'short', day: 'numeric' })}–${end.toLocaleDateString('en', start.getMonth() === end.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' })}`
        : null;

      const buildLabel =
        itin?.status === 'approved' ? 'Approved' :
        itin?.status === 'submitted' ? 'Waiting approval' :
        itin?.status === 'draft' ? 'Draft' : 'Not started';

      setMeta({
        status: raw.status as MatchStatus,
        travelerName: tp?.full_name ?? null,
        travelerAvatar: tp?.avatar_url ?? null,
        tripName: (content ? String(content.trip_name ?? '') || null : null) ?? portrait?.trip_name ?? null,
        destination: content ? String(content.destination ?? '') || null : null,
        dates,
        progress,
        buildLabel,
      });
    }

    return () => window.removeEventListener('sk:itinerary-saved', onItinerarySaved);
  }, [user, matchId]);

  if (!matchId) return null;

  const exit = (afterSave: boolean) => async () => {
    const go = pendingExit;
    if (!go) return;
    if (afterSave) {
      setLeaving(true);
      const ok = await guardRef.current?.save();
      setLeaving(false);
      if (!ok) return; // the editor shows the error; stay put
    }
    setPendingExit(null);
    go();
  };

  return (
    <UnsavedGuardProvider value={guardValue}>
    <div className="flex h-dvh bg-app-bg">
      <AppSidebarShell
        open={false}
        onClose={() => {}}
        logoHref="/host"
        headerSlot={meta ? (
          <MatchSwitcherCard
            matchId={matchId}
            meta={meta}
            trips={trips}
            onClose={() => {}}
          />
        ) : undefined}
      >
        <MatchNavContent
          matchId={matchId}
          meta={meta}
          onClose={() => {}}
        />
      </AppSidebarShell>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Phone header: back to the trips + Builder / Preview.
            (The desktop sidebar is never opened on a phone.) */}
        <header className="md:hidden bg-app-card border-b border-app-border shrink-0 pt-safe px-safe">
          <div className="flex items-center gap-1 px-2 pt-1">
            <button
              type="button"
              onClick={() => attempt(() => navigate('/host'))}
              aria-label="Back to trips"
              className="min-h-11 pl-1 pr-3 flex items-center gap-0.5 text-app-fg hover:text-app-muted transition-colors"
            >
              <i className="ti ti-chevron-left text-[22px]" />
              <span className="text-[15px] font-semibold">Trips</span>
            </button>
          </div>
          <div className="flex gap-6 px-4 border-t border-app-border">
            {([
              ['builder', 'Builder', `/host/itinerary/${matchId}`],
              ['preview', 'Preview', `/host/preview/${matchId}`],
            ] as const).map(([key, label, to]) => {
              const active = matchSectionFor(pathname) === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => attempt(() => navigate(to))}
                  aria-current={active ? 'page' : undefined}
                  className={`min-h-11 text-[13px] border-b-2 -mb-px transition-colors ${
                    active ? 'border-app-fg text-app-fg font-semibold' : 'border-transparent text-app-muted font-medium'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </header>

        {fullBleed ? (
          <main className="flex-1 min-h-0 flex flex-col">{children}</main>
        ) : (
          <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className={`${WIDTH_CLASSES[width]} mx-auto w-full`}>
              {children}
            </div>
          </main>
        )}

        {/* Same bottom bar as the other host screens — the builder / chat /
            preview pills above switch the part of the trip, this switches app section. */}
        <HostTabBar />
      </div>

      {pendingExit && (
        <ConfirmDialog
          title="Save your draft?"
          body="You have changes to this handbook that haven’t been saved yet."
          onCancel={() => setPendingExit(null)}
          actions={[
            { label: leaving ? 'Saving…' : 'Save draft', variant: 'primary', onClick: () => void exit(true)(), disabled: leaving },
            { label: 'Discard changes', onClick: () => void exit(false)(), disabled: leaving },
            { label: 'Keep editing', variant: 'ghost', onClick: () => setPendingExit(null), disabled: leaving },
          ]}
        />
      )}
    </div>
    </UnsavedGuardProvider>
  );
}

export default HostMatchLayout;
